import React from 'prop-types';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';

/**
 * ActivityTypeHelpModal - Explains activity type categorization
 */
export const ActivityTypeHelpModal = ({
  isOpen,
  onClose,
  activityTypeMapping,
  activityTypeScores,
  answeredQuestions,
  getScoreColor,
  getScoreDarkColor
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Activity Type Filter</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <p className="text-gray-700 leading-relaxed">
              Activity types categorize questions based on <strong>task completion priority</strong> and the strategic function they serve in your business. Understanding these categories helps you prioritize improvements and allocate resources effectively.
            </p>
          </div>

          {/* Show scores for each activity type */}
          <div className="space-y-3">
            <h3 className="font-bold text-lg text-gray-900">Your Activity Type Scores</h3>
            {answeredQuestions.length === 0 ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">No answered questions yet. Complete the assessment to see your scores.</p>
              </div>
            ) : (
              Object.keys(activityTypeMapping).map(activityType => {
                const score = activityTypeScores[activityType] || 0;
                const scoreColor = getScoreColor(score);
                const darkColor = getScoreDarkColor(score);
                return (
                  <div key={activityType} className="flex items-center justify-between p-3 rounded-lg border-2" style={{ backgroundColor: `${scoreColor}60`, borderColor: darkColor }}>
                    <span className="font-medium" style={{ color: darkColor }}>{activityType}</span>
                    <span className="text-xl font-bold" style={{ color: darkColor }}>{Math.round(score)}%</span>
                  </div>
                );
              })
            )}
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="font-bold text-gray-900 mb-2">De-Risking Activity</h3>
              <p className="text-sm text-gray-700">Questions that reduce business risk and protect value. These activities should typically be completed first as they prevent value destruction and make your business more stable and predictable.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">Strategy Activity</h3>
              <p className="text-sm text-gray-700">Questions related to long-term planning, market positioning, and competitive advantage. These define the direction of your business and should be addressed after de-risking activities.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">Efficiency Activity</h3>
              <p className="text-sm text-gray-700">Questions about operational optimization, process improvement, and resource utilization. These activities make your business run smoother and more profitably.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">Growth Activity</h3>
              <p className="text-sm text-gray-700">Questions focused on expansion, new markets, and scaling operations. These activities drive top-line revenue and should be prioritized once your foundation is solid.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">Culture Activity</h3>
              <p className="text-sm text-gray-700">Questions about organizational values, team dynamics, and workplace environment. These create the foundation for sustainable performance and employee retention.</p>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition min-h-[44px]"
          >
            Got it, thanks!
          </button>
        </div>
      </div>
    </div>
  );
};

ActivityTypeHelpModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  activityTypeMapping: PropTypes.object.isRequired,
  activityTypeScores: PropTypes.object.isRequired,
  answeredQuestions: PropTypes.array.isRequired,
  getScoreColor: PropTypes.func.isRequired,
  getScoreDarkColor: PropTypes.func.isRequired
};

/**
 * AssetTypeHelpModal - Explains intangible asset types
 */
export const AssetTypeHelpModal = ({
  isOpen,
  onClose,
  intangibleAssetMapping,
  intangibleAssetScores,
  answeredQuestions,
  getScoreColor,
  getScoreDarkColor
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Intangible Asset Type Filter</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
            <p className="text-gray-700 leading-relaxed">
              Intangible asset types represent the <strong>non-physical resources</strong> that create value in your business. These assets are often the most valuable components of a company and directly impact your exit valuation.
            </p>
          </div>

          {/* Show scores for each asset type */}
          <div className="space-y-3">
            <h3 className="font-bold text-lg text-gray-900">Your Intangible Asset Scores</h3>
            {answeredQuestions.length === 0 ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">No answered questions yet. Complete the assessment to see your scores.</p>
              </div>
            ) : (
              Object.keys(intangibleAssetMapping).map(assetType => {
                const score = intangibleAssetScores[assetType] || 0;
                const scoreColor = getScoreColor(score);
                const darkColor = getScoreDarkColor(score);
                return (
                  <div key={assetType} className="flex items-center justify-between p-3 rounded-lg border-2" style={{ backgroundColor: `${scoreColor}60`, borderColor: darkColor }}>
                    <span className="font-medium" style={{ color: darkColor }}>{assetType}</span>
                    <span className="text-xl font-bold" style={{ color: darkColor }}>{Math.round(score)}%</span>
                  </div>
                );
              })
            )}
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="font-bold text-gray-900 mb-2">Human Capital</h3>
              <p className="text-sm text-gray-700">The collective skills, knowledge, and capabilities of your workforce. This includes employee expertise, training programs, leadership quality, and organizational culture that drives performance.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">Structural Capital</h3>
              <p className="text-sm text-gray-700">The systems, processes, databases, and intellectual property that remain with the company. This includes proprietary software, documented procedures, patents, and organizational infrastructure.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">Customer Capital</h3>
              <p className="text-sm text-gray-700">The value of your customer relationships, brand reputation, and market position. This includes customer loyalty, retention rates, brand equity, and the strength of customer relationships.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">Social Capital</h3>
              <p className="text-sm text-gray-700">The value derived from networks, partnerships, and external relationships. This includes strategic alliances, supplier relationships, industry connections, and community reputation.</p>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-2">Revenue & Profitability</h3>
              <p className="text-sm text-gray-700">The financial performance and earnings capacity of your business. This includes revenue streams, profit margins, financial stability, and the predictability of future cash flows.</p>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition"
          >
            Got it, thanks!
          </button>
        </div>
      </div>
    </div>
  );
};

AssetTypeHelpModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  intangibleAssetMapping: PropTypes.object.isRequired,
  intangibleAssetScores: PropTypes.object.isRequired,
  answeredQuestions: PropTypes.array.isRequired,
  getScoreColor: PropTypes.func.isRequired,
  getScoreDarkColor: PropTypes.func.isRequired
};

/**
 * ImpactTypeHelpModal - Explains Readiness vs Attractiveness
 */
export const ImpactTypeHelpModal = ({
  isOpen,
  onClose,
  assessment,
  getScoreColor,
  getScoreDarkColor,
  getScoreLightBg
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Attractiveness & Readiness—Not the Same Thing</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Show current scores */}
          <div className="grid grid-cols-2 gap-4">
            <div
              className="border-2 p-6 rounded-lg"
              style={{
                backgroundColor: getScoreLightBg(assessment?.readiness_score || 0),
                borderColor: getScoreColor(assessment?.readiness_score || 0)
              }}
            >
              <h3
                className="text-sm font-semibold mb-2"
                style={{ color: getScoreDarkColor(assessment?.readiness_score || 0) }}
              >
                Exit Readiness Score
              </h3>
              <p
                className="text-4xl font-bold"
                style={{ color: getScoreColor(assessment?.readiness_score || 0) }}
              >
                {Math.round(assessment?.readiness_score || 0)}%
              </p>
              <p
                className="text-xs mt-2"
                style={{ color: getScoreDarkColor(assessment?.readiness_score || 0) }}
              >
                Inside-out view of your business
              </p>
            </div>
            <div
              className="border-2 p-6 rounded-lg"
              style={{
                backgroundColor: getScoreLightBg(assessment?.attractiveness_score || 0),
                borderColor: getScoreColor(assessment?.attractiveness_score || 0)
              }}
            >
              <h3
                className="text-sm font-semibold mb-2"
                style={{ color: getScoreDarkColor(assessment?.attractiveness_score || 0) }}
              >
                Business Attractiveness Score
              </h3>
              <p
                className="text-4xl font-bold"
                style={{ color: getScoreColor(assessment?.attractiveness_score || 0) }}
              >
                {Math.round(assessment?.attractiveness_score || 0)}%
              </p>
              <p
                className="text-xs mt-2"
                style={{ color: getScoreDarkColor(assessment?.attractiveness_score || 0) }}
              >
                Outside-in view from buyers
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
            <p className="text-gray-700 leading-relaxed font-medium">
              Sales and income may make your business look attractive, but that does not mean it's ready to grow and transition to unlock its wealth.
            </p>
          </div>

          <div className="space-y-4">
            <p className="text-gray-700 leading-relaxed">
              To grow your business and be positioned to transition it to unlock its wealth, your business not only needs to be <strong>attractive to the private capital market</strong>, but you also need to be able to prove you and it are <strong>ready to grow and transition</strong>. You need both.
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h3 className="font-bold text-indigo-900 mb-2">Attractiveness</h3>
                <p className="text-sm text-gray-700">How your business looks from the <strong>outside in</strong>. Assessed from a buyer's point of view based on financial performance, market position, and growth potential.</p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-bold text-blue-900 mb-2">Readiness</h3>
                <p className="text-sm text-gray-700">A view of your business from the <strong>inside out</strong>. Assessed from a buyer's point of view during due diligence when they look under the hood.</p>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <h3 className="font-bold text-red-900 mb-2">Why It Matters</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                If you achieve a decent score on <strong>Business Attractiveness</strong> but a lower score on <strong>Personal, Financial, and Business Readiness</strong>, you still have issues that lower your valuation. What typically happens is that someone from the outside looks at your business, and it has decent sales and income and therefore looks attractive. Given its attractiveness, they make an offer to buy it. At the time of the offer, they have only limited information—probably information you have presented in the best light possible.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mt-2">
                But once they get into the <strong>due diligence process</strong> and look under the hood, sit down at the wheel, and test drive it, they are disappointed. It looked good from the outside in (attractiveness), but once they looked deeper, it did not meet their expectations (readiness). <strong>Readiness is equally as important as attractiveness.</strong>
              </p>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
              <h3 className="font-bold text-blue-900 mb-2">State of Fact, Not State of Mind</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Business Attractiveness and Growth and Exit Readiness are a <strong>state of fact, not a state of mind</strong>. You can't just say your business is attractive and that you and it are ready. You must be able to <strong>prove it</strong>.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mt-2">
                When the private capital market looks at your business, it's unemotionally trying to understand where it places in the Range of Value. In other words, should they be paying a premium or getting a discount?
              </p>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
              <h3 className="font-bold text-yellow-900 mb-2">The Ugly Baby Syndrome</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Owners tend to look at their businesses much more personally and usually through rose-colored glasses. Their business is their baby. Even if their baby is ugly, it's still their ugly baby. As a result, you tend to <strong>overstate the value</strong> of your businesses.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mt-2">
                In fact, in a survey by the Alliance of Mergers and Acquisitions Advisors (AM&AA), <strong>95% of M&A advisors</strong> indicated an owner's perception of value versus its real market value is the <strong>number one reason they can't sell a business</strong>.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mt-2">
                To get a true read on the value of your business, you need to look at it <strong>unemotionally from the private capital market's viewpoint</strong>.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition min-h-[44px]"
          >
            Got it, thanks!
          </button>
        </div>
      </div>
    </div>
  );
};

ImpactTypeHelpModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  assessment: PropTypes.shape({
    readiness_score: PropTypes.number,
    attractiveness_score: PropTypes.number
  }),
  getScoreColor: PropTypes.func.isRequired,
  getScoreDarkColor: PropTypes.func.isRequired,
  getScoreLightBg: PropTypes.func.isRequired
};
